import { submitEvent } from '@otto-ec/tracking-bct';
import { getProductFeaturesForNonPdpPage } from '../../../_common/client/tracking/get-product-features-for-non-pdp-page';
import { ReviewSource } from '../../../_common/types/customer-review/review-submission-data';
import { ReviewSubmissionClientData_CanSubmit } from '../../types/review-submission-client-data';

const REVIEW_SOURCE_ASK_FOR_FURTHER_REVIEW: ReviewSource = 'askForFurtherReview';

type FollowUpReviewSuggestion = {
    productId: string;
    variationId: string;
    title: string;
    imageUrl: string;
    orderDate: string;
};

type LoadReviewSuggestionsParams = {
    successViewElement: Pick<HTMLElement, 'querySelector'>;
    clientData: ReviewSubmissionClientData_CanSubmit;
};

const ORDER_DATE_FORMATTER = new Intl.DateTimeFormat('de-DE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
});

export const loadReviewSuggestions = async ({
    successViewElement,
    clientData,
}: LoadReviewSuggestionsParams): Promise<number | undefined> => {
    const followUpReviewSuggestions = clientData.followUpReviewSuggestions;
    if (followUpReviewSuggestions?.toggleActive !== true) return;

    const suggestions = followUpReviewSuggestions.testGroupActive ? await fetchFollowUpReviewSuggestions(clientData.variation.id) : [];

    if (suggestions.length > 0) {
        const reviewSuggestionsElement = successViewElement.querySelector<HTMLElement>('.js_cr_submission-page__review-suggestions');
        if (reviewSuggestionsElement) {
            renderFollowUpReviewSuggestions(reviewSuggestionsElement, suggestions);
        }
    }

    return suggestions.length;
};

const fetchFollowUpReviewSuggestions = async (excludeVariationId: string): Promise<FollowUpReviewSuggestion[]> => {
    try {
        const response = await fetch(
            `/cr-review-submission/follow-up-review-suggestions/v1?excludeVariationId=${encodeURIComponent(excludeVariationId)}`,
            { cache: 'no-store' }
        );

        if (!response.ok) return [];

        const result: unknown = await response.json();
        if (!isRecord(result) || !isFollowUpReviewSuggestions(result.suggestions)) return [];
        return result.suggestions;
    } catch {
        return [];
    }
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isFollowUpReviewSuggestions = (value: unknown): value is FollowUpReviewSuggestion[] =>
    Array.isArray(value) && value.every(isFollowUpReviewSuggestion);

const isFollowUpReviewSuggestion = (value: unknown): value is FollowUpReviewSuggestion =>
    isRecord(value) &&
    typeof value.productId === 'string' &&
    typeof value.variationId === 'string' &&
    typeof value.title === 'string' &&
    typeof value.imageUrl === 'string' &&
    typeof value.orderDate === 'string' &&
    Number.isFinite(Date.parse(value.orderDate));

const renderFollowUpReviewSuggestions = (parentElement: HTMLElement, suggestions: FollowUpReviewSuggestion[]) => {
    const section = document.createElement('section');
    section.className = 'cr_follow-up-review-suggestions';

    const headline = document.createElement('h2');
    headline.className = 'oc-headline-100 cr_follow-up-review-suggestions__headline';
    headline.textContent = 'Deine offenen Bewertungen';
    section.appendChild(headline);

    const list = document.createElement('ul');
    list.className = 'cr_follow-up-review-suggestions__list';

    suggestions.forEach(suggestion => list.appendChild(createSuggestionItem(suggestion)));
    section.appendChild(list);
    parentElement.appendChild(section);
};

const createSuggestionItem = (suggestion: FollowUpReviewSuggestion): HTMLLIElement => {
    const item = document.createElement('li');
    item.className = 'cr_follow-up-review-suggestions__item';

    const image = document.createElement('img');
    image.className = 'cr_follow-up-review-suggestions__image';
    image.src = suggestion.imageUrl;
    image.alt = '';
    image.loading = 'lazy';
    item.appendChild(image);

    const content = document.createElement('div');
    content.className = 'cr_follow-up-review-suggestions__content';

    const title = document.createElement('h3');
    title.className = 'oc-copy-100 cr_follow-up-review-suggestions__title';
    title.textContent = suggestion.title;
    content.appendChild(title);

    const orderDate = document.createElement('time');
    orderDate.className = 'oc-copy-100 oc-copy--bold cr_follow-up-review-suggestions__order-date';
    orderDate.dateTime = suggestion.orderDate;
    orderDate.textContent = `Bestellt am ${formatOrderDate(suggestion.orderDate)}`;
    content.appendChild(orderDate);

    const button = document.createElement('oc-button-v1');
    button.className = 'cr_follow-up-review-suggestions__button';
    button.setAttribute('variant', 'secondary');
    button.setAttribute('size', '50');
    button.setAttribute('icon-type-left', 'rating-empty');
    button.setAttribute(
        'base64-href',
        btoa(
            `/kundenbewertung-abgabe?productId=${encodeURIComponent(suggestion.productId)}&variationId=${encodeURIComponent(
                suggestion.variationId
            )}&reviewSource=${encodeURIComponent(REVIEW_SOURCE_ASK_FOR_FURTHER_REVIEW)}`
        )
    );
    button.textContent = 'Artikel bewerten';
    button.addEventListener('click', () => {
        void submitEvent(
            { review_AskForFurtherReview: ['click'] },
            { name: 'click', features: getProductFeaturesForNonPdpPage(suggestion.variationId) }
        ).catch(() => undefined);
    });
    content.appendChild(button);

    item.appendChild(content);
    return item;
};

const formatOrderDate = (orderDate: string): string => ORDER_DATE_FORMATTER.format(new Date(orderDate));
